import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';

import { FALLBACK_LIMITS, useConfig } from '@entities/config/api';
import { useGuestSession } from '@entities/guest';
import { useCreateSearchSession } from '@entities/search-session';
import { useMe } from '@entities/user/api';
import { saveChatDraft } from '@features/chat';
import { NewSearchForm } from '@features/searches';
import { HERO_PROMPT_STORAGE_KEY, ROUTES } from '@shared/config/routes';
import { Button, Container, Skeleton, StateBlock } from '@shared/ui';

import { SearchesPage } from '../searches/SearchesPage';

import styles from './AppHomePage.module.css';

/** Запрос с главной живёт до создания подбора, а не до первого рендера (AC-01). */
function takeHeroPrompt(): string | null {
  try {
    const value = sessionStorage.getItem(HERO_PROMPT_STORAGE_KEY);
    if (value !== null) sessionStorage.removeItem(HERO_PROMPT_STORAGE_KEY);
    return value && value.trim() !== '' ? value.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Точка входа `/app` (FE-205, MVP_PRD §41.2).
 *
 * У зарегистрированного здесь список заказов, **у гостя списка нет**: `/app`
 * для него — это его единственный заказ. Поэтому экран разветвляется:
 *
 * * пользователь — обычный список «Мои заказы»;
 * * гость с заказом — сразу его заказ;
 * * гость без заказа с текстом с главной — сразу новый заказ и его диалог (AC-01);
 * * гость без заказа без текста — поле первого запроса, как в пустом состоянии списка.
 *
 * Показывать гостю список из одной карточки было бы обещанием второго заказа,
 * которого у него не будет: `MAX_GUEST_SEARCHES = 1`.
 */
export function AppHomePage() {
  const me = useMe();
  const guest = useGuestSession(!me.isPending && me.data == null);

  if (me.isPending) return <HomeSkeleton />;
  if (me.data != null) return <SearchesPage />;
  if (guest.isPending) return <HomeSkeleton />;
  if (guest.data == null) return <SearchesPage />;

  return <GuestHome orderId={guest.data.search_session_id} />;
}

function GuestHome({ orderId }: { orderId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const create = useCreateSearchSession();
  const { mutate } = create;
  const prompt = useRef<string | null>(null);
  const submitted = useRef(false);
  const autoStarted = useRef(false);

  // Запрос с главной читается один раз: повторное создание заказа при возврате
  // на `/app` было бы для пользователя необъяснимым.
  if (prompt.current === null) prompt.current = takeHeroPrompt();

  const submit = useCallback(
    (initialPrompt: string | null) => {
      if (submitted.current) return;
      submitted.current = true;
      mutate(
        { initialPrompt },
        {
          onSuccess: (state) => {
            void navigate(ROUTES.searchAssistant(state.session.id), { replace: true });
          },
          onError: () => {
            submitted.current = false;
          },
        },
      );
    },
    [mutate, navigate],
  );

  /**
   * AC-01: запрос с главной — это уже решение начать, и второго нажатия гость
   * делать не должен. Заказ создаётся сразу, текст становится первым
   * сообщением диалога — ровно как у вошедшего пользователя в `SearchesPage`.
   * Раньше гость попадал в форму с подставленным текстом и нажимал
   * «Bestellung starten» ещё раз. Один раз за монтирование — отсюда ref.
   */
  useEffect(() => {
    if (orderId !== null || autoStarted.current || prompt.current === null) return;
    autoStarted.current = true;
    submit(prompt.current);
  }, [orderId, submit]);

  if (orderId !== null) {
    // У гостя заказ ровно один (`MAX_GUEST_SEARCHES = 1`), и второй под текст с
    // главной не создаётся. Значит, текст обязан уехать в **этот** диалог:
    // раньше он читался из хранилища перед самым редиректом и пропадал молча —
    // человек писал запрос на главной и попадал в пустое поле ввода.
    //
    // Черновик, а не отправка: заказ гостя может стоять где угодно, вплоть до
    // готового рабочего набора, и дописывать в такой диалог сообщение за
    // человека — решение за него. Текст он видит в поле и отправляет сам.
    if (prompt.current !== null) saveChatDraft(orderId, prompt.current);
    return <Navigate to={ROUTES.searchAssistant(orderId)} replace />;
  }

  // Пока заказ под текст с главной создаётся, формы нет: она мелькнула бы с
  // уже отправленным текстом. Отказ сервера возвращает форму с тем же текстом
  // и кнопкой повтора — набранное не теряется.
  if (prompt.current !== null && !create.isError) return <HomeSkeleton />;

  return (
    <Container>
      <div className={styles.page}>
        <h1 className={styles.title}>{t('guest.home.title')}</h1>
        <p className={styles.lead}>{t('guest.home.lead')}</p>

        {/* Та же карточка, что у формы нового заказа в списке: одна композиция
            на оба входа (DESIGN_SYSTEM §5.2). Её кнопка — единственная красная. */}
        <div className={styles.panel}>
          <NewSearchForm
            initialValue={prompt.current ?? ''}
            pending={create.isPending}
            maxLength={
              config?.limits.chat_message_max_length ?? FALLBACK_LIMITS.chat_message_max_length
            }
            onSubmit={submit}
          />
        </div>

        {create.isError ? (
          <StateBlock
            tone="error"
            title={t('error.title')}
            description={t('error.description')}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  submitted.current = false;
                  submit(prompt.current);
                }}
              >
                {t('error.retry')}
              </Button>
            }
          />
        ) : null}
      </div>
    </Container>
  );
}

function HomeSkeleton() {
  const { t } = useTranslation();
  return (
    <Container>
      <div className={styles.page}>
        <p className="visually-hidden" role="status">
          {t('searches.loading')}
        </p>
        <Skeleton height="2rem" width="40%" />
        <Skeleton height="10rem" radius="var(--radius-card)" />
      </div>
    </Container>
  );
}
